import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCaretDown, FaInfoCircle } from "react-icons/fa";
import { useTheme } from 'styled-components';

export interface ddItem {
  id: string,
  name: string
}
type AutoSelectProps = {
  placeholder: string;
  initSelect: string,
  options: ddItem[];
  onSelect: (value: string) => void,
  emptyString: string,
  optionDetails?: (item: ddItem) => any,
  disabled: boolean
};

export const AutoSelectDropdown: React.FC<AutoSelectProps> = ({ placeholder, initSelect, options, onSelect, emptyString, optionDetails, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [userTyped, setUserTyped] = useState(false);

  const theme = useTheme();

  const filteredOptions =
    !userTyped || search.trim() === ""
      ? options // show full list when no user input or just cleared
      : options.filter((opt) =>
        opt.name.toLowerCase().includes(search.toLowerCase())
      );

  useEffect(() => {
    if (typeof initSelect !== 'undefined') {
      let indx = options.findIndex((x) => x.id === initSelect);
      if (indx === -1)
        indx = options.findIndex((x) => x.name === initSelect);
      if (indx > -1) {
        setHighlightedIndex(indx);
        setSearch(options[indx].name);
      }
      else {
        setHighlightedIndex(0);
        setSearch("");
      }
    }
  }, [initSelect, options]);

  const toggleDropdown = () => {
    if (disabled)
      return;
    setIsOpen((prev) => {
      const newState = !prev;

      if (newState && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Assume dropdown height ~160px
        if (spaceBelow < 160 && spaceAbove > 160) {
          setDropUp(true);
        } else {
          setDropUp(false);
        }
      }

      return newState;
    });
  };

  const handleSelect = (value: ddItem) => {
    setSearch(value.name);
    setIsOpen(false);
    setUserTyped(false);  // ✅ reset so dropdown shows full list again
    onSelect(value.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex((prev) =>
        (prev - 1 + filteredOptions.length) % filteredOptions.length
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const styles: { [key: string]: React.CSSProperties } = {
    ddIcon: {
      position: "absolute",
      right: "8px",
      fontSize: "16px",
      pointerEvents: "none",
      top: "50%",
      transform: "translateY(-50%)",
      color: theme.colors.text,
    },
    clearIcon: {
      position: 'absolute',
      right: '28px',
      fontSize: '14px',
      cursor: 'pointer',
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1,
      color: '#999',
      width: "20px",
      height: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "50%",
      transition: "all 0.2s ease",
    },
    container: {
      position: 'relative',
      width: '100%',
      fontFamily: 'Arial, sans-serif',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      border: `1px solid ${theme.colors.inputBorder}`,
      borderRadius: '4px',
      backgroundColor: theme.colors.card,
      paddingRight: "28px"
    },
    input: {
      flex: 1,
      height: "34px",
      padding: '0 8px',
      textIndent: "8px",
      border: '0px none',
      borderRadius: '4px',
      fontSize: '14px',
      backgroundColor: theme.colors.card,
      color: theme.colors.text,
    },
    dropdown: {
      position: 'absolute',
      left: 0,
      minWidth: '100%',
      width: 'max-content',
      maxWidth: '500px',
      border: `1px solid ${theme.colors.inputBorder}`,
      borderRadius: '4px',
      backgroundColor: theme.colors.card,
      listStyle: 'none',
      padding: '4px 0',
      maxHeight: '150px',
      overflowY: 'auto',
      zIndex: 10,
    },
    option: {
      padding: '8px 12px',
      cursor: 'pointer',
      color: theme.colors.text,
      whiteSpace: 'nowrap'
    },
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <div style={disabled ? {} : styles.inputWrapper} onClick={toggleDropdown}>
        <input
          style={disabled ? {} : styles.input}
          value={search}
          className={disabled ? 'form-control' : 'no-border'}
          disabled={disabled}
          placeholder={filteredOptions.length > 0 ? placeholder : emptyString}
          onChange={(e) => {
            const val = e.target.value;
            setSearch(val);
            setIsOpen(true);
            setUserTyped(true);   // ✅ mark that user typed
            if (val.trim() === "") {
              setUserTyped(false); // ✅ reset when cleared
              onSelect("");
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (!userTyped && initSelect) {
              // restore selected value if cleared
              const selected = options.find(
                (opt) => opt.id === initSelect || opt.name === initSelect
              );
              if (selected) {
                setSearch(selected.name);
              }
            }
          }}
        />
        {search && !disabled && (
          <span style={styles.clearIcon} className='clear-icon' onClick={() => {
            setSearch('');
            setIsOpen(false);
            onSelect('');
          }}>
            ×
          </span>
        )}
        {
          !disabled &&
          <FaCaretDown style={styles.ddIcon} />
        }
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            style={{
              ...styles.dropdown,
              top: dropUp ? 'auto' : '100%',
              bottom: dropUp ? '100%' : 'auto',
              marginTop: dropUp ? undefined : '4px',
              marginBottom: dropUp ? '4px' : undefined,
            }}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={option.id}
                  style={{
                    ...styles.option,
                    backgroundColor:
                      index === highlightedIndex
                        ? theme.colors.hoverBackground
                        : theme.colors.card,
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(option)}
                >
                  <div className='d-flex flex-row align-items-center gap-1'>
                    {option.name}
                    {
                      typeof optionDetails != 'undefined' &&
                      optionDetails(option)
                    }
                  </div>
                </li>
              ))
            ) : (
              <li style={styles.option}>{emptyString}</li>
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
